import { addSeconds, max, min } from "date-fns";

import { MIN_RECORDING_DURATION_MS } from "@/features/socratic/hooks/use-dialogue-controls";
import { StartedDialogue } from "@/features/socratic/types";

import { hasDialogueResponse } from "./has-dialogue-response";

export type DialoguePhase = "completed" | "expired" | "resume" | "fresh";

export interface ResolvedDialogueTimeline {
  phase: DialoguePhase;
  /** Server-stamped moment the dialogue's clock started. */
  startedAt: Date;
  /** When the response window opens. The recording start on a resume. */
  preparationEndDate: Date;
  /** When the response window closes. Never later than the dialogue's expiry. */
  responseDeadline: Date;
  /** When the student began recording, if they already have. */
  recordingStartedAt: Date | null;
}

/**
 * Work out where a dialogue stands, so a student who refreshes or reconnects
 * picks their attempt up with the time they have left.
 *
 * Resolved once and shared by the page and the redux slice. Computing the phase
 * and the dates separately lets them land on either side of the deadline and
 * render a screen with no recording and no way out.
 */
export function resolveDialogueTimeline(
  dialogue: StartedDialogue,
  now: Date
): ResolvedDialogueTimeline {
  const { readingTimeSeconds, responseTimeSeconds } = dialogue;

  const startedAt = new Date(dialogue.startedAt);
  const preparationEnd = addSeconds(startedAt, readingTimeSeconds);

  const [turn] = dialogue.turns;
  const recordingStartedAt =
    turn?.recordingStartedAt != null
      ? // A clock running behind would otherwise shorten the window.
        max([new Date(turn.recordingStartedAt), startedAt])
      : null;

  const effectiveStart = recordingStartedAt ?? preparationEnd;

  // `recordingStartedAt` comes from the student's own clock and the server
  // stores it as given, so it cannot be the only thing setting the deadline.
  // `expiredAt` is server-stamped, and is the ceiling. Where the server did not
  // stamp one, fall back to the formula it uses.
  const expiredAt =
    dialogue.expiredAt != null
      ? new Date(dialogue.expiredAt)
      : addSeconds(preparationEnd, responseTimeSeconds);

  const responseDeadline = min([
    addSeconds(effectiveStart, responseTimeSeconds),
    expiredAt,
  ]);

  return {
    phase: resolvePhase(),
    startedAt,
    preparationEndDate: recordingStartedAt ?? preparationEnd,
    responseDeadline,
    recordingStartedAt,
  };

  function resolvePhase(): DialoguePhase {
    if (hasDialogueResponse(dialogue)) return "completed";

    // Too little of the window left to record an answer worth submitting.
    const remainingMs = responseDeadline.getTime() - now.getTime();
    if (remainingMs < MIN_RECORDING_DURATION_MS) return "expired";

    if (recordingStartedAt != null || now >= preparationEnd) return "resume";

    return "fresh";
  }
}
